import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Sitemark from './SitemarkIcon.tsx';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

export default function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const openSection = React.useCallback((sectionName) => {
    setOpen(false);
    const selectedHomeSection = document.getElementById(sectionName);
    if (selectedHomeSection) {
      selectedHomeSection.scrollIntoView({ 
        behavior: 'smooth',
      });
    } else {
      navigate("/", {
        state: {
          selectedHomeSection: sectionName,
        }
      })
    }
  }, [navigate]);
  
  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters  sx={{ bgcolor: 'white'}}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <Sitemark />
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button variant="text" color="primary" size="medium" onClick={() => openSection('features')}>
                Services
              </Button>
              <Button variant="text" color="info" size="medium" onClick={() => openSection('highlights')}>
                Highlights
              </Button>
              <Button variant="text" color="info" size="medium" onClick={() => openSection('pricing')}>
                Pricing
              </Button>
              <Button variant="text" color="info" size="medium" sx={{ minWidth: 0 }} onClick={() => openSection('faq')}>
                FAQ
              </Button>
              <RouterLink to="/blogs">
                <Button variant="text" color="info" size="medium" sx={{ minWidth: 0 }}>
                    Blogs
                </Button>
              </RouterLink>
              <RouterLink to="/videos">
                <Button variant="text" color="info" size="medium" sx={{ minWidth: 0 }}>
                  Videos
                </Button>
              </RouterLink>
              <RouterLink to="/legal">
                <Button variant="text" color="info" size="medium" sx={{ minWidth: 0 }}>
                  Legal
                </Button>
              </RouterLink>
              <RouterLink to="/aboutus">
                <Button variant="text" color="info" size="medium" sx={{ minWidth: 0 }}>
                    About Us
                </Button>
              </RouterLink>
              <RouterLink to="/products/syteflow/learn-more">
                <Button variant="text" color="info" size="medium" sx={{ minWidth: 0 }}>
                    Products
                </Button>
              </RouterLink>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                <MenuItem onClick={() => openSection('features')}>Services</MenuItem>
                <MenuItem onClick={() => openSection('highlights')}>Highlights</MenuItem>
                <MenuItem onClick={() => openSection('pricing')}>Pricing</MenuItem>
                <MenuItem onClick={() => openSection('faq')}>FAQ</MenuItem>
                <RouterLink to="/blogs">
                   <MenuItem onClick={toggleDrawer(false)}>Blogs</MenuItem>
                </RouterLink>
                <RouterLink to="/videos" onClick={toggleDrawer(false)}>
                  <MenuItem>Videos</MenuItem>
                </RouterLink>
                <RouterLink to="/legal" >
                   <MenuItem onClick={toggleDrawer(false)}>Legal</MenuItem>
                </RouterLink>
                <RouterLink to="/aboutus" >
                   <MenuItem onClick={toggleDrawer(false)}>About Us</MenuItem>
                </RouterLink>
                <RouterLink to="/products/syteflow/learn-more" >
                   <MenuItem onClick={toggleDrawer(false)}>Products</MenuItem>
                </RouterLink>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
